import { Link } from "react-router-dom";
import { Lock, Sparkles, ArrowRight } from "lucide-react";

/**
 * Paywall — shown in place of a paid feature when the user's trial has
 * ended and they haven't subscribed (or when a Standard user opens Pro-only).
 *
 * Props:
 *   reason: "trial_ended" | "pro_required"
 *   featureName: e.g. "Voter List"
 */
export default function Paywall({ reason = "trial_ended", featureName = "this feature" }) {
  const isPro = reason === "pro_required";
  return (
    <div className="max-w-2xl mx-auto px-4 py-16" data-testid="paywall">
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-8 sm:p-10 text-center">
        <span className="inline-flex h-14 w-14 items-center justify-center rounded-xl bg-flag-red text-white">
          <Lock className="h-7 w-7" />
        </span>
        <h1 className="mt-5 font-display text-3xl font-black text-flag-navy">
          {isPro ? `${featureName} is a Pro feature` : "Your trial has ended"}
        </h1>
        <p className="mt-3 text-slate-600 leading-relaxed">
          {isPro ? (
            <>Upgrade from Standard to <strong>Pro</strong> to unlock {featureName}. Pro gives you everything in Standard plus the full Voter List CRM and the Finances suite (fundraising, expenses, budgets, transactions, reimbursements, and the report builder with CSV export).</>
          ) : (
            <>Your 7-day free trial of Electable has wrapped. Pick a plan to keep running your campaign.</>
          )}
        </p>
        <div className="mt-7 grid sm:grid-cols-2 gap-3 text-left">
          <div className="border-2 border-slate-200 rounded-xl p-5">
            <div className="flex items-baseline justify-between">
              <h2 className="font-display text-xl font-bold text-flag-navy">Standard</h2>
              <span className="text-2xl font-black text-flag-navy">$34.99<span className="text-sm font-semibold text-slate-500">/mo</span></span>
            </div>
            <p className="mt-2 text-sm text-slate-600">Everything except the Voter List and Finances tools.</p>
          </div>
          <div className="border-2 border-flag-red rounded-xl p-5 bg-flag-red/[0.03]">
            <div className="flex items-baseline justify-between">
              <h2 className="font-display text-xl font-bold text-flag-navy flex items-center gap-1.5">
                Pro <Sparkles className="h-4 w-4 text-flag-red" />
              </h2>
              <span className="text-2xl font-black text-flag-navy">$49.99<span className="text-sm font-semibold text-slate-500">/mo</span></span>
            </div>
            <p className="mt-2 text-sm text-slate-600">Standard + Voter List CRM + Finances suite.</p>
          </div>
        </div>
        <Link
          to="/billing"
          data-testid="paywall-cta"
          className="mt-7 inline-flex items-center gap-2 bg-flag-red hover:bg-flag-red-hover text-white font-bold rounded-lg px-6 py-3 transition-colors"
        >
          Choose a plan <ArrowRight className="h-4 w-4" />
        </Link>
        <p className="mt-4 text-xs text-slate-500">
          Already have an access code? Enter it on the <Link to="/billing" className="underline">billing page</Link>.
        </p>
      </div>
    </div>
  );
}
